import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  listAdminComplaints,
  type ComplaintCategory,
  type ComplaintStatus,
  type ComplaintVisibility,
  type PublicComplaint,
  type SortOrder,
} from '../../lib/complaints-api';
import { listClusterBlocks } from '../../lib/cluster-api';
import { extractApiError } from '../../lib/api-client';
import { formatDate, formatDateTime } from '../../lib/format';
import {
  COMPLAINT_CATEGORY_KEY,
  COMPLAINT_STATUS_KEY,
  ComplaintCategoryChip,
  ComplaintStatusChip,
} from './chips';
import { useT } from '../../i18n';

const STATUS_OPTIONS: ReadonlyArray<ComplaintStatus> = [
  'NEW',
  'IN_PROGRESS',
  'RESOLVED',
  'CANCELLED',
];
const CATEGORY_OPTIONS: ReadonlyArray<ComplaintCategory> = [
  'FACILITY_DAMAGE',
  'CLEANLINESS',
  'NOISE',
  'LIGHTING',
  'SECURITY',
  'OTHER',
];
const VISIBILITY_OPTIONS: ReadonlyArray<ComplaintVisibility> = ['PUBLIC', 'PRIVATE'];

type StatusFilter = ComplaintStatus | 'ALL';
type CategoryFilter = ComplaintCategory | 'ALL';
type VisibilityFilter = ComplaintVisibility | 'ALL';
type BlockFilter = string | 'ALL';

const PAGE_SIZE = 50;

export function ComplaintsListPage() {
  const t = useT();
  const [items, setItems] = useState<PublicComplaint[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('ALL');
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('ALL');
  const [blockFilter, setBlockFilter] = useState<BlockFilter>('ALL');
  const [thisMonth, setThisMonth] = useState(false);
  const [sort, setSort] = useState<SortOrder>('newest');

  const [blocks, setBlocks] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      try {
        setBlocks(await listClusterBlocks());
      } catch {
        // non-fatal; block filter just stays empty
        setBlocks([]);
      }
    })();
  }, []);

  async function loadFirstPage() {
    setLoading(true);
    setError(null);
    try {
      const out = await listAdminComplaints({
        limit: PAGE_SIZE,
        sort,
        ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
        ...(categoryFilter !== 'ALL' ? { category: categoryFilter } : {}),
        ...(visibilityFilter !== 'ALL' ? { visibility: visibilityFilter } : {}),
        ...(blockFilter !== 'ALL' ? { block: blockFilter } : {}),
        ...(thisMonth ? { this_month: true } : {}),
      });
      setItems(out.items);
      setNextCursor(out.nextCursor);
    } catch (e) {
      setError(extractApiError(e).message);
      setItems([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    setError(null);
    try {
      const out = await listAdminComplaints({
        limit: PAGE_SIZE,
        cursor: nextCursor,
        sort,
        ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
        ...(categoryFilter !== 'ALL' ? { category: categoryFilter } : {}),
        ...(visibilityFilter !== 'ALL' ? { visibility: visibilityFilter } : {}),
        ...(blockFilter !== 'ALL' ? { block: blockFilter } : {}),
        ...(thisMonth ? { this_month: true } : {}),
      });
      setItems((prev) => [...prev, ...out.items]);
      setNextCursor(out.nextCursor);
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    loadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, categoryFilter, visibilityFilter, blockFilter, thisMonth, sort]);

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h4" fontWeight={600}>
          {t('comp.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('comp.subtitle')}
        </Typography>
      </div>

      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
          >
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="filter-status">{t('common.status')}</InputLabel>
              <Select
                labelId="filter-status"
                label={t('common.status')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <MenuItem value="ALL">{t('common.all')}</MenuItem>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(COMPLAINT_STATUS_KEY[s] ?? s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="filter-category">{t('comp.filterCategory')}</InputLabel>
              <Select
                labelId="filter-category"
                label={t('comp.filterCategory')}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as CategoryFilter)}
              >
                <MenuItem value="ALL">{t('common.all')}</MenuItem>
                {CATEGORY_OPTIONS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {t(COMPLAINT_CATEGORY_KEY[c] ?? c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="filter-visibility">{t('comp.filterVisibility')}</InputLabel>
              <Select
                labelId="filter-visibility"
                label={t('comp.filterVisibility')}
                value={visibilityFilter}
                onChange={(e) => setVisibilityFilter(e.target.value as VisibilityFilter)}
              >
                <MenuItem value="ALL">{t('common.all')}</MenuItem>
                {VISIBILITY_OPTIONS.map((v) => (
                  <MenuItem key={v} value={v}>
                    {t(v === 'PUBLIC' ? 'comp.visPublic' : 'comp.visPrivate')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="filter-block">{t('comp.filterBlock')}</InputLabel>
              <Select
                labelId="filter-block"
                label={t('comp.filterBlock')}
                value={blockFilter}
                onChange={(e) => setBlockFilter(e.target.value)}
              >
                <MenuItem value="ALL">{t('common.all')}</MenuItem>
                {blocks.map((b) => (
                  <MenuItem key={b} value={b}>
                    {t('comp.blockPrefix', { block: b })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={thisMonth}
                  onChange={(e) => setThisMonth(e.target.checked)}
                  size="small"
                />
              }
              label={t('comp.thisMonth')}
            />
            <Box sx={{ flexGrow: 1 }} />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="filter-sort">{t('comp.sortLabel')}</InputLabel>
              <Select
                labelId="filter-sort"
                label={t('comp.sortLabel')}
                value={sort}
                onChange={(e) => setSort(e.target.value as SortOrder)}
              >
                <MenuItem value="newest">{t('comp.sortNewest')}</MenuItem>
                <MenuItem value="oldest">{t('comp.sortOldest')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('comp.empty')}</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('comp.colTitle')}</TableCell>
                      <TableCell sx={{ width: 130 }}>{t('common.status')}</TableCell>
                      <TableCell sx={{ width: 150 }}>{t('comp.filterCategory')}</TableCell>
                      <TableCell sx={{ width: 120 }}>{t('comp.colBlockUnit')}</TableCell>
                      <TableCell sx={{ width: 110 }}>{t('comp.colVisibility')}</TableCell>
                      <TableCell sx={{ width: 110 }}>{t('comp.colObserved')}</TableCell>
                      <TableCell sx={{ width: 170 }}>{t('comp.colFiled')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            component={RouterLink}
                            to={`/complaints/${c.id}`}
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {c.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {c.location_detail.slice(0, 60)}
                            {c.location_detail.length > 60 ? '…' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <ComplaintStatusChip status={c.status} />
                        </TableCell>
                        <TableCell>
                          <ComplaintCategoryChip category={c.category} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {c.unit_block ? t('comp.blockPrefix', { block: c.unit_block }) : '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.unit_number ?? ''} {c.filer_name ? `· ${c.filer_name}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={c.visibility === 'PUBLIC' ? 'text.primary' : 'text.secondary'}
                          >
                            {t(c.visibility === 'PUBLIC' ? 'comp.visPublic' : 'comp.visPrivate')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(c.date_observed)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(c.created_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {nextCursor ? (
                <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                  <Button onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? t('common.loadingEllipsis') : t('common.loadMore')}
                  </Button>
                </Box>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
